import { fillRegionAgainstReference, type BrushPoint, type DrawingPixels } from './brush';

/** The latest local Fill action. Region selection and the pre-fill pixels stay
 * frozen; opacity changes composite once from that original, never from a fill. */
export class FillEdit {
  private constructor(private before: DrawingPixels, private region: Uint8Array, private color: [number, number, number]) {}

  static create(before: DrawingPixels, point: BrushPoint, color: string, reference?: Uint8ClampedArray, eligible?: Uint8Array): FillEdit | null {
    const { width, height } = before, count = width * height;
    const hex = color.replace('#', ''), normalized = /^[\da-f]{3}$/i.test(hex) ? hex.split('').map(value => value + value).join('') : hex;
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || count > 4_194_304 || before.data.length !== count * 4 || !/^[\da-f]{6}$/i.test(normalized)) return null;
    const boundaries = new Uint8ClampedArray(reference ?? before.data);
    const pixels = new Uint8ClampedArray(count * 4);
    if (!fillRegionAgainstReference(pixels, boundaries, width, height, point, '#ffffff', 1, 20, eligible)) return null;
    const region = new Uint8Array(count);
    for (let pixel = 0; pixel < count; pixel++) region[pixel] = pixels[pixel * 4 + 3] ? 1 : 0;
    return new FillEdit({ width, height, data: new Uint8ClampedArray(before.data) }, region,
      [parseInt(normalized.slice(0, 2), 16), parseInt(normalized.slice(2, 4), 16), parseInt(normalized.slice(4, 6), 16)]);
  }

  contains(point: BrushPoint): boolean {
    const x = Math.floor(point.x), y = Math.floor(point.y);
    return Number.isFinite(x) && Number.isFinite(y) && x >= 0 && y >= 0 && x < this.before.width && y < this.before.height && this.region[y * this.before.width + x] === 1;
  }

  render(opacity: number) {
    if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) throw new Error('Fill opacity must be between zero and one.');
    const data = new Uint8ClampedArray(this.before.data);
    if (opacity > 0) for (let pixel = 0; pixel < this.region.length; pixel++) {
      if (!this.region[pixel]) continue;
      const offset = pixel * 4, retained = this.before.data[offset + 3] / 255 * (1 - opacity), alpha = opacity + retained;
      for (let channel = 0; channel < 3; channel++) data[offset + channel] = retained === 0 ? this.color[channel] : Math.round((this.color[channel] * opacity + this.before.data[offset + channel] * retained) / alpha);
      data[offset + 3] = Math.round(alpha * 255);
    }
    return { width: this.before.width, height: this.before.height, data };
  }
}


export interface FillAdjustmentSession {
  edit: FillEdit; color: string; wallAware: boolean; paintOrder: import('../shared/types').PaintOrder;
  belowPlacementId?: string; opacity: number;
}
interface FillAdjustmentContext {
  kind: string; shape: boolean; color: string; wallAware: boolean;
  paintOrder: import('../shared/types').PaintOrder; belowPlacementId?: string; opacity: number;
}
/** Leaving Fill retires its adjustment session without rewriting authored pixels.
 * Shapes keep the remembered brush kind, so their context must end it explicitly. */
export function adjustFillSession(session: FillAdjustmentSession | null, context: FillAdjustmentContext): { session: FillAdjustmentSession | null; pixels?: ReturnType<FillEdit['render']> } {
  if (!session || context.shape || context.kind !== 'fill' || context.color !== session.color || context.wallAware !== session.wallAware) return { session: null };
  // Moving the whole contribution preserves this operation's frozen region and pixels.
  const positioned = context.paintOrder === session.paintOrder && context.belowPlacementId === session.belowPlacementId
    ? session : { ...session, paintOrder: context.paintOrder, belowPlacementId: context.belowPlacementId };
  if (context.opacity === session.opacity) return { session: positioned };
  return { session: { ...positioned, opacity: context.opacity }, pixels: session.edit.render(context.opacity) };
}
